import {
  ClauseMiningDryRunIdRequestSchema,
  ENDPOINTS,
  RequestClauseMiningDryRunRequestSchema,
  type ApproveClauseMiningDryRunResponse,
  type CancelClauseMiningDryRunResponse,
  type ClauseMiningDryRunFilter,
  type GetClauseMiningDryRunResponse,
  type ListClauseMiningDryRunsResponse,
  type RequestClauseMiningDryRunResponse,
  type RunClauseMiningDryRunNowResponse,
} from '@clm/shared-types';

import { apiGet, apiPost } from './client';
import { substitutePath } from './verifications';

/**
 * The six clause-mining dry-run calls behind the Pipeline page's **Clause
 * mining** section — CLM-103 (first slice).
 *
 * Same recipe as `pipeline-runs.ts`: the path comes from the registry, every
 * request is `.parse`d before it leaves the browser (the schemas are
 * `.strict()`), and every response is **parsed** against the registry schema
 * so an off-contract answer is a defect to report, not data to show.
 *
 * Runs are org-level — no scope key is ever encoded here; the action check is
 * the API's. A 409 on request / run-now carries `dry_run_already_pending` in
 * `field_errors[0].message` (one queue slot); a 403 on approve / run-now means
 * the caller is not an admin. None of these calls starts the run itself — the
 * API-side worker claims an approved row within its poll interval, and every
 * row this slice can produce is a dry run (nothing here can ask for a live
 * one — `RequestClauseMiningDryRunInput` carries no `dry_run` field).
 */

const REQUEST = ENDPOINTS['request-clause-mining-dry-run'];
const RUN_NOW = ENDPOINTS['run-clause-mining-dry-run-now'];
const APPROVE = ENDPOINTS['approve-clause-mining-dry-run'];
const CANCEL = ENDPOINTS['cancel-clause-mining-dry-run'];
const LIST = ENDPOINTS['list-clause-mining-dry-runs'];
const GET = ENDPOINTS['get-clause-mining-dry-run'];

export interface RequestClauseMiningDryRunInput {
  readonly filter: ClauseMiningDryRunFilter;
  readonly reason: string;
}

function options(signal: AbortSignal | undefined): { readonly signal: AbortSignal } | undefined {
  return signal === undefined ? undefined : { signal };
}

/** `POST /v1/clause-mining/dry-runs` — a contributor's request; `status: requested` until an admin approves. */
export async function requestClauseMiningDryRun(
  request: RequestClauseMiningDryRunInput,
  signal?: AbortSignal,
): Promise<RequestClauseMiningDryRunResponse> {
  const validated = RequestClauseMiningDryRunRequestSchema.parse(request);
  const raw = await apiPost<unknown>(REQUEST.path, validated, options(signal));
  return REQUEST.response.parse(raw);
}

/** `POST /v1/clause-mining/dry-runs/now` — admin only: create and approve in one action. */
export async function runClauseMiningDryRunNow(
  request: RequestClauseMiningDryRunInput,
  signal?: AbortSignal,
): Promise<RunClauseMiningDryRunNowResponse> {
  const validated = RequestClauseMiningDryRunRequestSchema.parse(request);
  const raw = await apiPost<unknown>(RUN_NOW.path, validated, options(signal));
  return RUN_NOW.response.parse(raw);
}

/** `POST /v1/clause-mining/dry-runs/{id}/approve` — admin only. Empty body; the id rides in the path. */
export async function approveClauseMiningDryRun(
  id: string,
  signal?: AbortSignal,
): Promise<ApproveClauseMiningDryRunResponse> {
  const { clause_mining_dry_run_id } = ClauseMiningDryRunIdRequestSchema.parse({ clause_mining_dry_run_id: id });
  const raw = await apiPost<unknown>(substitutePath(APPROVE.path, { clause_mining_dry_run_id }), {}, options(signal));
  return APPROVE.response.parse(raw);
}

/** `POST /v1/clause-mining/dry-runs/{id}/cancel` — own requested row, or any active row for an admin. */
export async function cancelClauseMiningDryRun(
  id: string,
  signal?: AbortSignal,
): Promise<CancelClauseMiningDryRunResponse> {
  const { clause_mining_dry_run_id } = ClauseMiningDryRunIdRequestSchema.parse({ clause_mining_dry_run_id: id });
  const raw = await apiPost<unknown>(substitutePath(CANCEL.path, { clause_mining_dry_run_id }), {}, options(signal));
  return CANCEL.response.parse(raw);
}

/** `GET /v1/clause-mining/dry-runs` — active, pending and the last twenty terminal runs. */
export async function listClauseMiningDryRuns(signal?: AbortSignal): Promise<ListClauseMiningDryRunsResponse> {
  const raw = await apiGet<unknown>(LIST.path, options(signal));
  return LIST.response.parse(raw);
}

/** `GET /v1/clause-mining/dry-runs/{id}` — one row; the active card polls it. */
export async function getClauseMiningDryRun(id: string, signal?: AbortSignal): Promise<GetClauseMiningDryRunResponse> {
  const { clause_mining_dry_run_id } = ClauseMiningDryRunIdRequestSchema.parse({ clause_mining_dry_run_id: id });
  const raw = await apiGet<unknown>(substitutePath(GET.path, { clause_mining_dry_run_id }), options(signal));
  return GET.response.parse(raw);
}
